/**
 * Code that will detect what techonolgy will be used by current user based on various variables
 * like web browser, javascript API support etc.
 */
class BarcodeSupport {

    /**
     * Contains the error messages after function hasSupport() has run.
     * @private
     */
    private errors:string[];

    /**
     * The error messages. These messages can be replaced on constructor.
     * @private
     */
    private errorTemplate:BarcodeSupportErrorMessages = {
        barcodeDetectorErrorMessage: "Your web browser does not support the Barcode Detection API",
        webcameraMissingErrorMessage: "You do not have a webcamera",
        httpErrorMessage: "You need to be on encrypted protocol (HTTPS) to use barcode detection",
        mediaDevicesErrorMessage: "You do not have support for webcams",
        samsungInternetBrowserSupportErrorMessage: "It seems you are using Samsung Internet browser. Please use Chrome for better barcode support."
    };

    constructor(errors?:BarcodeSupportErrorMessages) {
        this.errors = [];

        // if object is given, then replace the error messages
        if (typeof errors !== "undefined") {
            this.errorTemplate = errors;
        }
    }

    /**
     * Check if browser has support for the Barcode Detection API.
     * This API is currently only supported by Chrome version 83 and above on Android and
     * Samsung Internet version 13 an above.
     *
     * Here is a list of supported devices:
     * @see https://developer.mozilla.org/en-US/docs/Web/API/Barcode_Detection_API#browser_compatibility
     *
     * Here is the API documention:
     * @see https://developer.mozilla.org/en-US/docs/Web/API/Barcode_Detection_API
     *
     * @private
     */
    private hasBarcodeDetectionApiSupport():boolean {
        //let has = (!("BarcodeDetector" in window));
        // @ts-ignore
        const has = typeof BarcodeDetector!=="undefined";
        if (!has) {
            this.errors.push(this.errorTemplate.barcodeDetectorErrorMessage);
        }
        return has;
    }

    /**
     * The Barcode Detection API will only work on HTTPS.
     * @private
     */
    private hasWebcamSupport():boolean {
        const has = navigator.mediaDevices!==undefined;
        if (!has) {
            this.errors.push(this.errorTemplate.mediaDevicesErrorMessage);
        }
        return has;
    }

    /**
     * Check if user has web camera
     * @private
     */
    private hasWebCam():boolean {
        let md = navigator.mediaDevices;
        if (!md || !md.enumerateDevices) { return false; }
        else {
            md.enumerateDevices().then(devices => {
                return (devices.some(device => 'videoinput' === device.kind));
            });
        }
    }

    /**
     * Test if the user is using Samsung Internet browser.
     * We are having problems scanning barcodes with this browser, so warning user about this.
     * @private
     */
    private hasSamsungInternetBrowser():boolean {
        const isSamsungBrowser = navigator.userAgent.indexOf("SamsungBrowser") > -1;
        if (isSamsungBrowser) {
            this.errors.push(this.errorTemplate.samsungInternetBrowserSupportErrorMessage);
        }
        return isSamsungBrowser;
    }

    /**
     * Check if user has given permission to the webcam. This function used callbacks, and
     * I needed just the boolean right away. Waiting with this.
     * @private
     */
    // private hasWebcamPermission():void {
    //     navigator.getUserMedia({video: true}, function() {
    //         return true;
    //     }, function() {
    //         return false;
    //     });
    // }

    /**
     * Check that user is visiting on encrypted protocol
     * @private
     */
    private hasHttps():boolean {
        const has = window.location.protocol==="https:";
        if (!has) {
            this.errors.push(this.errorTemplate.httpErrorMessage);
        }
        return has;
    }

    /**
     * Does browser have support for the Barcode Detection API?
     */
    public hasSupport():boolean {
        this.errors = [];           // empty array
        let checkedHttps = false;   // Flag to avoid running this.hasHttps() more than once

        // check if api is not available
        if (!this.hasBarcodeDetectionApiSupport()) {
            // If API is not available, check if user is on HTTP, because the API
            // "is available only in secure contexts in some or all supporting browsers":
            // https://developer.mozilla.org/en-US/docs/Web/API/Barcode_Detection_API#browser_compatibility
            if (!this.hasHttps()) { }
            checkedHttps = true;
        }

        // Check if browser has support for web cameras
        if (!this.hasWebcamSupport()) {
            // and then check for existence of a web camera
            if (!this.hasWebCam()) { }

            // The lack of support for webcameras can be because the user is not in a secure context. We
            // do a check for https if it wasn't tested before, to give a warning to user in case.
            if (!checkedHttps) { this.hasHttps(); }

            // TODO we also need to check that the user has given permission to use the camera.
            // In cases where user has denied permission, it would be a great thing to inform
            // about this to avoid time spent on support.
        }

        // Give warning if Samsung Internet browser
        //if (this.hasSamsungInternetBrowser()) { }

        return this.errors.length==0;
    }

    /**
     * Get error messages after running hasSupport().
     */
    public getErrorMessages():string[] {
        return this.errors;
    }

    public getErrorMessagesAsString():string {
        return this.errors.join(". ");
    }

}

/**
 * define the type of the error message collection
 */
type BarcodeSupportErrorMessages = {
    barcodeDetectorErrorMessage:string,
    httpErrorMessage:string,
    mediaDevicesErrorMessage:string,
    webcameraMissingErrorMessage:string,
    samsungInternetBrowserSupportErrorMessage:string
};